import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';

const initialState = {
  jobs: {},
  executions: {},
};

const endpoints = build => ({
  // List all jobs with statistics
  listJobs: build.query({
    query: () => ({
      url: 'v1/batch/jobs',
    }),
    transformResponse: response => response.data,
    providesTags: () => [{ type: 'BatchJobs', id: 'LIST' }],
  }),

  // Get available jobs that can be triggered
  getAvailableJobs: build.query({
    query: () => ({
      url: 'v1/batch/jobs/available',
    }),
    transformResponse: response => response.data,
    providesTags: () => [{ type: 'AvailableJobs', id: 'LIST' }],
  }),

  // Get currently running jobs with queue stats
  getRunningJobs: build.query({
    query: () => ({
      url: 'v1/batch/jobs/running',
    }),
    transformResponse: response => {
      const data = response.data;
      // Normalize executionId: API returns _id, WebSocket events use executionId
      if (data?.running) {
        data.running = data.running.map(job => ({
          ...job,
          executionId: job.executionId || job._id?.toString() || job._id,
        }));
      }
      return data;
    },
    providesTags: () => [{ type: 'RunningJobs', id: 'LIST' }],
  }),

  // Get detailed statistics for a specific job
  getJobStats: build.query({
    query: ({ jobName, days = 30 }) => ({
      url: `v1/batch/jobs/stats?jobName=${encodeURIComponent(jobName)}&days=${days}`,
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, { jobName }) => [{ type: 'JobStats', id: jobName }],
  }),

  // List job executions with filtering
  listExecutions: build.query({
    query: ({ jobName, status, batchConfigId, limit = 50, offset = 0, days } = {}) => {
      const params = new URLSearchParams();
      if (jobName) params.append('jobName', jobName);
      if (status) params.append('status', status);
      if (batchConfigId) params.append('batchConfigId', batchConfigId);
      if (limit) params.append('limit', limit);
      if (offset) params.append('offset', offset);
      if (days) params.append('days', days);
      return {
        url: `v1/batch/jobs/executions?${params.toString()}`,
      };
    },
    transformResponse: response => response.data,
    providesTags: result => [
      ...(result?.executions || []).map(({ _id }) => ({ type: 'JobExecution', id: _id })),
      { type: 'JobExecution', id: 'LIST' },
    ],
  }),

  // Get specific execution details
  getExecution: build.query({
    query: id => ({
      url: `v1/batch/jobs/executions/${id}`,
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, id) => [{ type: 'JobExecution', id }],
  }),

  // Get failed jobs
  getFailures: build.query({
    query: ({ jobName, limit = 50, offset = 0, days } = {}) => {
      const params = new URLSearchParams();
      if (jobName) params.append('jobName', jobName);
      if (limit) params.append('limit', limit);
      if (offset) params.append('offset', offset);
      if (days) params.append('days', days);
      return {
        url: `v1/batch/jobs/failures?${params.toString()}`,
      };
    },
    transformResponse: response => response.data,
    providesTags: () => [{ type: 'FailedJobs', id: 'LIST' }],
  }),

  // Manually trigger a batch job
  triggerJob: build.mutation({
    query: ({ jobName, body }) => ({
      url: `v1/batch/jobs/${encodeURIComponent(jobName)}/trigger`,
      method: 'POST',
      body,
    }),
    invalidatesTags: () => [
      { type: 'RunningJobs', id: 'LIST' },
      { type: 'BatchJobs', id: 'LIST' },
      { type: 'JobExecution', id: 'LIST' },
    ],
  }),

  // Retry a failed job execution
  retryExecution: build.mutation({
    query: id => ({
      url: `v1/batch/jobs/executions/${id}/retry`,
      method: 'POST',
    }),
    invalidatesTags: (result, error, id) => [
      { type: 'JobExecution', id },
      { type: 'JobExecution', id: 'LIST' },
      { type: 'FailedJobs', id: 'LIST' },
      { type: 'RunningJobs', id: 'LIST' },
    ],
  }),
});

const BatchSlice = createApi({
  reducerPath: 'batchReducer',
  baseQuery,
  tagTypes: ['BatchJobs', 'AvailableJobs', 'RunningJobs', 'JobStats', 'JobExecution', 'FailedJobs'],
  initialState,
  keepUnusedDataFor: 30,
  endpoints,
});

export const {
  useListJobsQuery,
  useGetAvailableJobsQuery,
  useGetRunningJobsQuery,
  useGetJobStatsQuery,
  useListExecutionsQuery,
  useGetExecutionQuery,
  useGetFailuresQuery,
  useTriggerJobMutation,
  useRetryExecutionMutation,
} = BatchSlice;

export const { middleware: batchMiddleware, reducer: batchReducer } = BatchSlice;
export default BatchSlice; // Export the API instance for util.updateQueryData
